import {
  DISPLAY_STATUS_LABEL_STATES,
  type DisplayStatusLabelResult,
  type DisplayStatusLabelState,
  type DisplayStatusSortKey,
  type DisplayStatusViewer,
  type ResolveDisplayStatusLabelInput,
} from './types';

const SORT_KEYS: Record<DisplayStatusLabelState, DisplayStatusSortKey> = {
  [DISPLAY_STATUS_LABEL_STATES.DRAFT]: 'draft',
  [DISPLAY_STATUS_LABEL_STATES.SENT]: 'sent',
  [DISPLAY_STATUS_LABEL_STATES.VIEWED]: 'viewed',
  [DISPLAY_STATUS_LABEL_STATES.PARTIALLY_SIGNED]: 'partially_signed',
  [DISPLAY_STATUS_LABEL_STATES.AWAITING_PAYMENT]: 'awaiting_payment',
  [DISPLAY_STATUS_LABEL_STATES.PAID]: 'paid',
  [DISPLAY_STATUS_LABEL_STATES.PARTIALLY_PAID]: 'partially_paid',
  [DISPLAY_STATUS_LABEL_STATES.COMPLETED]: 'completed',
  [DISPLAY_STATUS_LABEL_STATES.AWAITING_YOUR_SIGNATURE]: 'awaiting_your_signature',
  [DISPLAY_STATUS_LABEL_STATES.AWAITING_CLIENT_SIGNATURE]: 'awaiting_client_signature',
  [DISPLAY_STATUS_LABEL_STATES.AWAITING_BUSINESS_OWNER_SIGNATURE]: 'awaiting_your_signature',
  [DISPLAY_STATUS_LABEL_STATES.PAID_AWAITING_SIGNATURE]: 'awaiting_client_signature',
  [DISPLAY_STATUS_LABEL_STATES.SIGNED]: 'signed',
  [DISPLAY_STATUS_LABEL_STATES.DECLINED]: 'declined',
  [DISPLAY_STATUS_LABEL_STATES.SCHEDULED]: 'scheduled',
  [DISPLAY_STATUS_LABEL_STATES.SUBMITTED]: 'submitted',
  [DISPLAY_STATUS_LABEL_STATES.FAILED]: 'failed',
  [DISPLAY_STATUS_LABEL_STATES.VOIDED]: 'sent',
  [DISPLAY_STATUS_LABEL_STATES.EXPIRED]: 'expired',
  [DISPLAY_STATUS_LABEL_STATES.OVERDUE]: 'overdue',
  [DISPLAY_STATUS_LABEL_STATES.PAYMENT_DUE]: 'sent',
  [DISPLAY_STATUS_LABEL_STATES.IN_PROGRESS]: 'in_progress',
  [DISPLAY_STATUS_LABEL_STATES.NEW]: 'viewed',
  [DISPLAY_STATUS_LABEL_STATES.IN_REVIEW]: 'in_progress',
  [DISPLAY_STATUS_LABEL_STATES.APPROVED]: 'completed',
};

const BUSINESS_LABELS: Record<DisplayStatusLabelState, string> = {
  [DISPLAY_STATUS_LABEL_STATES.DRAFT]: 'Draft',
  [DISPLAY_STATUS_LABEL_STATES.SENT]: 'Sent',
  [DISPLAY_STATUS_LABEL_STATES.VIEWED]: 'Viewed',
  [DISPLAY_STATUS_LABEL_STATES.PARTIALLY_SIGNED]: 'Partially Signed',
  [DISPLAY_STATUS_LABEL_STATES.AWAITING_PAYMENT]: 'Awaiting Payment',
  [DISPLAY_STATUS_LABEL_STATES.PAID]: 'Paid',
  [DISPLAY_STATUS_LABEL_STATES.PARTIALLY_PAID]: 'Partially Paid',
  [DISPLAY_STATUS_LABEL_STATES.COMPLETED]: 'Completed',
  [DISPLAY_STATUS_LABEL_STATES.AWAITING_YOUR_SIGNATURE]: 'Awaiting Your Signature',
  [DISPLAY_STATUS_LABEL_STATES.AWAITING_CLIENT_SIGNATURE]: 'Awaiting Client Signature',
  [DISPLAY_STATUS_LABEL_STATES.AWAITING_BUSINESS_OWNER_SIGNATURE]: 'Awaiting Your Signature',
  [DISPLAY_STATUS_LABEL_STATES.PAID_AWAITING_SIGNATURE]: 'Awaiting Client Signature',
  [DISPLAY_STATUS_LABEL_STATES.SIGNED]: 'Signed',
  [DISPLAY_STATUS_LABEL_STATES.DECLINED]: 'Declined',
  [DISPLAY_STATUS_LABEL_STATES.SCHEDULED]: 'Scheduled',
  [DISPLAY_STATUS_LABEL_STATES.SUBMITTED]: 'Submitted',
  [DISPLAY_STATUS_LABEL_STATES.FAILED]: 'Failed',
  [DISPLAY_STATUS_LABEL_STATES.VOIDED]: 'Voided',
  [DISPLAY_STATUS_LABEL_STATES.EXPIRED]: 'Expired',
  [DISPLAY_STATUS_LABEL_STATES.OVERDUE]: 'Payment overdue',
  [DISPLAY_STATUS_LABEL_STATES.PAYMENT_DUE]: 'Payment due',
  [DISPLAY_STATUS_LABEL_STATES.IN_PROGRESS]: 'In Progress',
  [DISPLAY_STATUS_LABEL_STATES.NEW]: 'New',
  [DISPLAY_STATUS_LABEL_STATES.IN_REVIEW]: 'In Review',
  [DISPLAY_STATUS_LABEL_STATES.APPROVED]: 'Approved',
};

const CLIENT_LABELS: Partial<Record<DisplayStatusLabelState, string>> = {
  [DISPLAY_STATUS_LABEL_STATES.AWAITING_YOUR_SIGNATURE]: 'Awaiting Your Signature',
  [DISPLAY_STATUS_LABEL_STATES.AWAITING_CLIENT_SIGNATURE]: 'Awaiting Your Signature',
  [DISPLAY_STATUS_LABEL_STATES.AWAITING_BUSINESS_OWNER_SIGNATURE]:
    'Awaiting Business Owner Signature',
  [DISPLAY_STATUS_LABEL_STATES.PAID_AWAITING_SIGNATURE]: 'Awaiting Your Signature',
};

/**
 * Resolve canonical display label + sortKey for a shared vocabulary state.
 */
export function resolveDisplayStatusLabel(
  input: ResolveDisplayStatusLabelInput
): DisplayStatusLabelResult {
  const { state, viewer } = input;
  const sortKey = SORT_KEYS[state];
  const clientLabel = viewer === 'client' ? CLIENT_LABELS[state] : undefined;
  const label = clientLabel ?? BUSINESS_LABELS[state];

  return { label, sortKey };
}

export function formatPartiallyPaidDetail(paid: number, total: number): string | undefined {
  if (total <= 0) {
    return undefined;
  }
  return `${paid}/${total} paid`;
}
